import type { AxiosError, AxiosRequestConfig } from 'axios';
import axios from 'axios';

import { APP_CONST } from '../constants';

import { refreshToken } from './requests';

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

export const api = axios.create({
  baseURL: import.meta.env.VITE_BASE_URL
});

api.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem(
      APP_CONST.LOCAL_STORAGE.AUTH_TOKEN
    );
    if (accessToken) {
      config.headers.Authorization = `Bearer ${JSON.parse(accessToken)}`;
    }
    return config;
  },
  (error) => {
    throw error;
  }
);

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & {
      _retry?: boolean;
    };

    if (
      error.response?.status === 401 &&
      originalRequest.url?.includes('/login')
    ) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve) => {
          refreshSubscribers.push((token: string) => {
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(api(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const token = localStorage.getItem(
          APP_CONST.LOCAL_STORAGE.REFRESH_TOKEN
        );
        if (!token) throw new Error('No refresh token available');

        const { data } = await refreshToken({
          params: {
            refreshToken: JSON.parse(
              localStorage.getItem(
                APP_CONST.LOCAL_STORAGE.REFRESH_TOKEN
              ) as string
            ) as string
          }
        });

        localStorage.setItem(
          APP_CONST.LOCAL_STORAGE.AUTH_TOKEN,
          JSON.stringify(data.token)
        );
        localStorage.setItem(
          APP_CONST.LOCAL_STORAGE.REFRESH_TOKEN,
          JSON.stringify(data.refreshToken)
        );

        api.defaults.headers.Authorization = `Bearer ${data.token}`;

        refreshSubscribers.forEach((callback) => callback(data.token));
        refreshSubscribers = [];

        return await api(originalRequest);
      } catch (err) {
        err;
        localStorage.removeItem('profile');
        localStorage.removeItem(APP_CONST.LOCAL_STORAGE.REFRESH_TOKEN);
        localStorage.removeItem(APP_CONST.LOCAL_STORAGE.AUTH_TOKEN);
        window.location.href = '/';
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);
